import React, { useEffect, useState, useCallback } from "react";
import { Copy, RefreshCw, Rss, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

export default function CalendarIntegration() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const load = useCallback(() => api.get("/admin/ical-feeds").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const copy = async (url, id) => {
    try { await navigator.clipboard.writeText(url); setCopied(id); toast.success("Feed URL copied"); setTimeout(() => setCopied(""), 1500); }
    catch { toast.error("Could not copy"); }
  };
  const regen = async () => {
    if (!window.confirm("Regenerate the feed URL? Existing OTA imports using the old URL will stop updating until you paste the new one.")) return;
    setBusy(true);
    try { const r = await api.post("/admin/ical-feeds/regenerate"); setData(r.data); toast.success("New feed URL generated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggle = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/ical-feeds/toggle", { enabled: !data.enabled }); setData(r.data); toast.success(r.data.enabled ? "Feeds enabled" : "Feeds disabled"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <Loader2 className="animate-spin text-rose mx-auto my-8" />;
  const Row = ({ label, url, id }) => (
    <div className="flex items-center gap-2 bg-sand/50 rounded-lg px-3 py-2" data-testid={`feed-row-${id}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-forest">{label}</div>
        <div className="text-xs text-stone-500 font-mono truncate">{data.enabled ? url : "— feeds disabled —"}</div>
      </div>
      <button onClick={() => copy(url, id)} disabled={!data.enabled} data-testid={`feed-copy-${id}`} className="p-2 rounded-md bg-white border border-stone-200 hover:bg-sand disabled:opacity-40">
        {copied === id ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}
      </button>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="calendar-integration">
      <div className="card-lux p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2"><Rss size={18} className="text-rose" /><h3 className="font-serif text-xl text-forest">Shareable iCal feeds</h3></div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${data.enabled ? "bg-green-100 text-green-700" : "bg-stone-200 text-stone-500"}`} data-testid="feed-status">{data.enabled ? "Enabled" : "Disabled"}</span>
            <button onClick={toggle} disabled={busy} data-testid="feed-toggle" className="text-xs px-3 py-1.5 rounded-full bg-forest text-cream font-semibold disabled:opacity-50">{data.enabled ? "Disable" : "Enable"}</button>
            <button onClick={regen} disabled={busy} data-testid="feed-regenerate" className="text-xs px-3 py-1.5 rounded-full bg-rose/10 text-rose font-semibold inline-flex items-center gap-1 disabled:opacity-50"><RefreshCw size={13} /> Regenerate</button>
          </div>
        </div>
        <p className="text-xs text-stone-500 mt-2">Paste these live availability feeds into Airbnb, Vrbo, Google Calendar, Outlook or Apple Calendar. They update automatically as bookings and blocks change. Compatible with any iCal (.ics) importer.</p>
      </div>

      <div className="card-lux p-5">
        <h4 className="font-serif text-lg text-forest mb-3">Whole property</h4>
        <Row label="Entire house (all suites)" url={data.house_url} id="house" />
      </div>

      <div className="card-lux p-5">
        <h4 className="font-serif text-lg text-forest mb-3">Per suite</h4>
        <div className="space-y-2">
          {data.rooms.map((r) => <Row key={r.id} label={r.name} url={r.url} id={r.id} />)}
          {data.rooms.length === 0 && <p className="text-sm text-stone-400">No suites yet.</p>}
        </div>
      </div>
    </div>
  );
}
